import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Carousel } from 'antd';
import type { CarouselRef } from 'antd/es/carousel';
import { ArrowCircleLeftIcon, ArrowCircleRightIcon, XIcon, ChevronRightIcon } from '../icons';
import { PhotoActionBar } from './PhotoActionBar';
import { PhotoOverlayInfo, PhotoThumbnailInfo, PhotoMetadata } from './PhotoOverlayInfo';
import { handleGalleryImageLoad } from './galleryImageLoad';
import './PhotoGallery.css';

export interface PhotoGridProps {
  /**
   * Array of photo URLs to display
   */
  photos: string[];
  /**
   * Maximum number of photos to display in the grid (default: 9)
   */
  maxPhotos?: number;
  /**
   * Callback when "View All" is clicked
   */
  onViewAll?: () => void;
  /**
   * Callback when "Add Photo" is clicked
   */
  onAddPhoto?: () => void;
  /**
   * Title for the section (default: "Photos")
   */
  title?: string;
  /**
   * Whether to show the "View All" link (default: true if photos > maxPhotos)
   */
  showViewAll?: boolean;
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Per-photo metadata for the lightbox overlay (should match photos array length)
   */
  photoMetadata?: PhotoMetadata[];
  /**
   * Show the user-info lockup in the lightbox overlay
   */
  showOverlayUserInfo?: boolean;
  /**
   * Show the event-info lockup in the lightbox overlay
   */
  showOverlayEventInfo?: boolean;
  /**
   * Index of a photo to highlight with a blue border (auto-fades via CSS)
   */
  highlightedIndex?: number | null;
}

/**
 * PhotoGrid - A 3x3 grid of photo thumbnails with optional "View All" link
 * 
 * Displays up to `maxPhotos` (default 9) in a 3-column grid.
 * Clicking any photo opens a full-screen lightbox gallery with ALL photos navigable via swipe.
 * The "View All" link triggers `onViewAll` callback (typically opens PhotoGalleryDrawer).
 */
export const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  maxPhotos = 9,
  onViewAll,
  onAddPhoto,
  title = 'Photos',
  showViewAll,
  className,
  photoMetadata,
  showOverlayUserInfo = false,
  showOverlayEventInfo = false,
  highlightedIndex,
}) => {
  const displayPhotos = photos.slice(0, maxPhotos);
  const hasMorePhotos = photos.length > maxPhotos;
  const shouldShowViewAll = showViewAll ?? hasMorePhotos;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<CarouselRef>(null);
  const isAnimatingRef = useRef(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openGallery = (index: number) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeGallery = () => {
    setIsModalOpen(false);
  };

  const goToPrev = () => {
    if (isAnimatingRef.current) return;
    if (currentIndex <= 0) return;
    isAnimatingRef.current = true;
    carouselRef.current?.goTo(currentIndex - 1);
  };

  const goToNext = () => {
    if (isAnimatingRef.current) return;
    if (currentIndex >= photos.length - 1) return;
    isAnimatingRef.current = true;
    carouselRef.current?.goTo(currentIndex + 1);
  };

  const handleBeforeChange = (current: number, next: number) => {
    // Only set animating if there's an actual slide change
    if (current !== next) {
      isAnimatingRef.current = true;
    }
  };

  const handleAfterChange = (current: number) => {
    // Clear any pending timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    // Use a small delay to ensure animation is truly complete before allowing next navigation
    animationTimeoutRef.current = setTimeout(() => {
      isAnimatingRef.current = false;
    }, 100);
    setCurrentIndex(current);
  };

  // Keyboard navigation and body scroll lock
  useEffect(() => {
    if (!isModalOpen) return;

    // Lock body scroll
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentIndex > 0) {
          goToPrev();
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (currentIndex < photos.length - 1) {
          goToNext();
        }
      } else if (e.key === 'Escape') {
        closeGallery();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isModalOpen, currentIndex, photos.length]);

  if (photos.length === 0) {
    return (
      <div className={`photo-grid-section photo-grid-section--empty ${className || ''}`}>
        <p className="photo-grid-empty-text">No pics yet. Be the first to add one!</p>
        {onAddPhoto && (
          <PhotoActionBar onClick={onAddPhoto} />
        )}
      </div>
    );
  }

  return (
    <>
      <div className={`photo-grid-section ${className || ''}`}>
        <div className="photo-grid">
          {displayPhotos.map((src, index) => (
            <div key={index} className={`photo-grid-item${index === highlightedIndex ? ' photo-grid-item--highlighted' : ''}`}>
              <img
                src={src}
                style={{ 
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                }}
                onClick={() => openGallery(index)}
                alt={`Photo ${index + 1}`}
              />
              {photoMetadata && <PhotoThumbnailInfo metadata={photoMetadata[index]} />}
            </div>
          ))}
        </div>
        {shouldShowViewAll && onViewAll && (
          <div className="photo-grid__view-all-row">
            <Button size="small" onClick={onViewAll}>
              See All
              <ChevronRightIcon size="s" tone="white" />
            </Button>
          </div>
        )}
        {onAddPhoto && (
          <PhotoActionBar onClick={onAddPhoto} />
        )}
      </div>

      {isModalOpen && createPortal(
        <div className="photo-gallery-overlay">
          {/* Backdrop - click to close */}
          <div 
            className="photo-gallery-backdrop" 
            onClick={closeGallery}
            aria-hidden="true"
          />

          {/* Close button */}
          <button
            className="photo-gallery-close"
            onClick={closeGallery}
            aria-label="Close gallery"
          >
            <XIcon size="m" tone="white" />
          </button>

          {/* Navigation arrows */}
          <button
            className="photo-gallery-arrow photo-gallery-arrow-prev"
            onClick={goToPrev}
            disabled={currentIndex === 0}
            style={{ opacity: currentIndex === 0 ? 0.3 : 1 }}
            aria-label="Previous photo"
          >
            <ArrowCircleLeftIcon size="xl" tone="white" />
          </button>

          <button
            className="photo-gallery-arrow photo-gallery-arrow-next"
            onClick={goToNext}
            disabled={currentIndex === photos.length - 1}
            style={{ opacity: currentIndex === photos.length - 1 ? 0.3 : 1 }}
            aria-label="Next photo"
          >
            <ArrowCircleRightIcon size="xl" tone="white" />
          </button>

          {/* Carousel */}
          <div className="photo-gallery-carousel-wrapper">
            <Carousel
              ref={carouselRef}
              initialSlide={currentIndex}
              dots={false}
              swipeToSlide={false}
              draggable={false}
              beforeChange={handleBeforeChange}
              afterChange={handleAfterChange}
              speed={300}
              waitForAnimate
            >
              {photos.map((src, idx) => (
                <div key={idx} className="photo-gallery-slide" onClick={closeGallery}>
                  <div className="photo-gallery-image-wrapper">
                    <img
                      src={src}
                      alt={`Photo ${idx + 1}`}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      onLoad={handleGalleryImageLoad}
                      style={{
                        userSelect: 'none',
                        WebkitUserDrag: 'none',
                      } as React.CSSProperties}
                    />
                    {photoMetadata && (showOverlayUserInfo || showOverlayEventInfo) && (
                      <PhotoOverlayInfo
                        showUserInfo={showOverlayUserInfo}
                        showEventInfo={showOverlayEventInfo}
                        metadata={photoMetadata[idx]}
                      />
                    )}
                  </div>
                </div>
              ))}
            </Carousel>
          </div>

          {/* Dot pagination */}
          <div className="photo-gallery-dots">
            {photos.map((_, idx) => (
              <span
                key={idx}
                className={`photo-gallery-dot ${idx === currentIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PhotoGrid;
